import { useCallback, useEffect, useState } from 'react';

/**
 * THE WHOLE ROUTER: twenty-three routes — twenty-two fixed and ONE parametrised (§26b's
 * ticket thread) — `history.pushState` and `popstate`.
 *
 * # Why not react-router
 *
 * The precedent is `apps/fsm/src/router.ts`, whose header states the trade this app
 * inherits: react-router-dom is ~20 kB for a handful of paths, and what actually
 * matters on a handset is that the BACK GESTURE leaves the sub-page and returns to the
 * previous one — which is `popstate`, and which a state-machine "router" holding a
 * `useState` gets wrong. `apps/admin` uses react-router because it has ~200 routes; the
 * two subscriber-facing apps do not.
 *
 * # Why paths and not a hash
 *
 * `internal/app/spa.go`'s `mountSPA` serves `index.html` for any path that is not a
 * real file, so `/password` boots the SPA when the core serves the bundle — which is
 * the ordinary deployment (`BILLING_PA_STATIC_DIR`). Vite's dev server does the same.
 *
 * ⚠ THE ONE DEPLOYMENT THIS COSTS SOMETHING: `BILLING_PA_HTTP_ENABLED=true` with the
 * bundle served by an nginx root, where a refresh on `/password` needs
 * `try_files $uri /index.html`. That is one line of documented configuration, and it is
 * the price of a URL a subscriber can be told over the phone. Recorded here rather than
 * discovered.
 *
 * ⚠ AN UNKNOWN PATH RENDERS HOME AND DOES NOT 404. That is deliberate and it is what
 * `mountSPA` makes possible: a subscriber given a URL with a typo, or a path a later wave
 * will add, lands on a page that works instead of on nothing. It is also why
 * `/payments/2026` is home rather than the payment history — a sub-path this build does
 * not define is not a path it should guess at.
 */

export type RouteKind =
  | 'home'
  | 'topup'
  | 'instructions'
  | 'state'
  | 'usage'
  | 'payments'
  | 'promises'
  | 'installments'
  | 'documents'
  | 'profile'
  | 'blockings'
  | 'devices'
  | 'accountPassword'
  | 'password'
  | 'tickets'
  | 'ticket'
  | 'news'
  | 'alerts'
  | 'tv'
  | 'tariff'
  | 'services'
  | 'statistics'
  | 'recover';

/**
 * A route, and the ONE route that carries an id.
 *
 * ⚠ `ticket` IS THE FIRST PARAMETRISED PATH IN THIS APP AND IT EARNS ITS EXCEPTION. §26b's
 * thread is a sub-page a subscriber lives in — he reads it, replies, attaches a photograph
 * — and the alternative, holding «which ticket is open» in component state, breaks precisely
 * the thing this file's header says a hand-rolled router must get right: the BACK GESTURE.
 * From a state-held thread, back leaves the whole section (or the portal), which on a
 * handset reads as a bug; a reload loses the thread; and the URL cannot be read out to
 * support. `/tickets/12` is a path this build DEFINES, which is why it is not covered by the
 * «a sub-path of a real section is home» rule the arm in `routing.test.tsx` pins for
 * `/payments/2026` — that rule is about paths this app does not define, and it still holds
 * for `/tickets/abc`.
 */
export type Route = { kind: RouteKind; id?: number };

/**
 * The path of every route, in ONE table.
 *
 * It is a table rather than a switch because `parseRoute` and `routePath` must agree, and
 * two switches are two places to forget an entry — a route printable but unparseable is a
 * link that works until the subscriber refreshes the page.
 */
const PATHS: ReadonlyArray<{ kind: RouteKind; path: string }> = [
  { kind: 'home', path: '/' },
  // ⚠ `/topup` AND NOT `/payment`, because `/payments` is already the HISTORY (§22) and
  // two paths differing by one letter are two paths a subscriber reads back to support
  // wrongly. `/instructions` is §8e's reading page — the route a suspended subscriber
  // reaches when a card payment is impossible.
  { kind: 'topup', path: '/topup' },
  { kind: 'instructions', path: '/instructions' },
  { kind: 'state', path: '/state' },
  { kind: 'usage', path: '/usage' },
  { kind: 'payments', path: '/payments' },
  { kind: 'promises', path: '/promises' },
  { kind: 'installments', path: '/installments' },
  { kind: 'documents', path: '/documents' },
  // ⚠ W4's four paths, and two of them are named against the grain on purpose.
  // `/account-password` and not `/password/account`: a sub-path of a route this app
  // ALREADY defines would be parsed as `home` by the table above (an unknown path renders
  // home), so a subscriber given the URL over the telephone would land on the dashboard
  // and report that the link is broken. And `/devices` rather than `/my-devices`, because
  // it is the word a subscriber reads back — the possessive belongs in the LABEL.
  { kind: 'profile', path: '/profile' },
  { kind: 'blockings', path: '/blockings' },
  { kind: 'devices', path: '/devices' },
  { kind: 'accountPassword', path: '/account-password' },
  { kind: 'password', path: '/password' },
  // ⚠ P2's four sections. `/tickets` is the LIST and `/tickets/{id}` the thread, which is
  // the one path in this table with a parameter — see the `Route` type for why it is worth
  // an exception. `/news` is «Сообщения» (the announcements) and `/alerts` is «Оповещения»
  // (the matrix), which are two different questions a subscriber asks: «что мне сообщили»
  // and «о чём мне сообщать». `/tv` and not `/24tv`, because a path beginning with a digit
  // is one a subscriber mis-hears over the telephone, and the brand belongs in the LABEL.
  { kind: 'tickets', path: '/tickets' },
  { kind: 'ticket', path: '/tickets' },
  { kind: 'news', path: '/news' },
  { kind: 'alerts', path: '/alerts' },
  { kind: 'tv', path: '/tv' },
  // ⚠ P3's three paths, and each is the SINGULAR-OR-PLURAL word a subscriber reads back over
  // the telephone. `/tariff` singular because an Account has one primary tariff and this
  // section moves that one — not a collection; `/services` plural because §17 is three lists;
  // `/statistics` and not `/stats`, because an abbreviation is a word he has to spell.
  // ⚠ NONE OF THEM IS A SUB-PATH OF AN EXISTING ROUTE, which is the rule the W4 block above
  // records: `/statistics` is not under `/state`, so neither can swallow the other, and a typo
  // in either lands on home rather than on the wrong section.
  { kind: 'tariff', path: '/tariff' },
  { kind: 'services', path: '/services' },
  { kind: 'statistics', path: '/statistics' },
  { kind: 'recover', path: '/recover' },
];

/** The thread's own path shape, so `parseRoute` and `routePath` cannot disagree about it. */
const TICKET_PATH = /^\/tickets\/(\d+)$/;

export function parseRoute(pathname: string): Route {
  const trimmed = pathname.replace(/\/+$/, '') || '/';
  const thread = TICKET_PATH.exec(trimmed);
  if (thread) {
    const id = Number(thread[1]);
    // A positive integer only. `/tickets/0` and `/tickets/00` are not ids this API can
    // answer for, and rendering a thread screen that must then 404 is worse than landing on
    // the list he can navigate from.
    if (Number.isSafeInteger(id) && id > 0) return { kind: 'ticket', id };
    return { kind: 'tickets' };
  }
  const hit = PATHS.find((p) => p.path === trimmed);
  return { kind: hit ? hit.kind : 'home' };
}

export function routePath(route: Route): string {
  // The thread's path is built from the id it carries; without one there is no thread to
  // print, so it prints the LIST — a link that lands somewhere real rather than on
  // `/tickets/undefined`.
  if (route.kind === 'ticket') {
    return route.id !== undefined && route.id > 0 ? `/tickets/${route.id}` : '/tickets';
  }
  return PATHS.find((p) => p.kind === route.kind)?.path ?? '/';
}

export interface Navigation {
  route: Route;
  /** `replace` leaves no history entry — used when the app corrects the URL itself. */
  navigate: (route: Route, options?: { replace?: boolean }) => void;
}

export function useRoute(): Navigation {
  const [route, setRoute] = useState<Route>(() => parseRoute(window.location.pathname));

  useEffect(() => {
    const onPop = () => setRoute(parseRoute(window.location.pathname));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const navigate = useCallback((next: Route, options?: { replace?: boolean }) => {
    const path = routePath(next);
    if (path !== window.location.pathname) {
      if (options?.replace) window.history.replaceState(null, '', path);
      else window.history.pushState(null, '', path);
    }
    setRoute(next);
    window.scrollTo(0, 0);
  }, []);

  return { route, navigate };
}
